import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

const navItems = [
  { label: '首页', to: '/' },
  { label: '关于', to: '/about' },
];

const categoryItems = [
  { label: '电影', to: '/category/movie', icon: 'ri-film-line' },
  { label: '短视频', to: '/category/short', icon: 'ri-video-line' },
  { label: '娱乐', to: '/category/entertainment', icon: 'ri-mic-line' },
];

export default function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const [keyword, setKeyword] = useState('');

  useEffect(() => {
    setMobileOpen(false);
    setCatOpen(false);
  }, [location.pathname]);

  const isActive = (to: string) =>
    to === '/' ? location.pathname === '/' : location.pathname.startsWith(to);

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : '/search');
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-background-200/70 bg-background-50/95 backdrop-blur">
      <div className="mx-auto flex h-16 w-full items-center gap-4 px-4 md:px-8">
        {/* Brand */}
        <Link to="/" className="flex shrink-0 items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 text-background-50">
            <i className="ri-play-circle-fill text-xl" />
          </span>
          {location.pathname === '/' ? (
            <h1 className="font-heading text-xl font-extrabold tracking-tight text-foreground-950">
              涩涩<span className="text-primary-500">视频</span>
            </h1>
          ) : (
            <span className="font-heading text-xl font-extrabold tracking-tight text-foreground-950">
              涩涩<span className="text-primary-500">视频</span>
            </span>
          )}
        </Link>

        {/* Desktop nav */}
        <nav className="ml-2 hidden items-center gap-1 md:flex">
          {navItems.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={`cursor-pointer rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                isActive(item.to)
                  ? 'bg-primary-100 text-primary-700'
                  : 'text-foreground-700 hover:bg-background-100 hover:text-foreground-950'
              }`}
            >
              {item.label}
            </Link>
          ))}
          <div
            className="relative"
            onMouseEnter={() => setCatOpen(true)}
            onMouseLeave={() => setCatOpen(false)}
          >
            <button
              type="button"
              className={`flex cursor-pointer items-center gap-1 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                location.pathname.startsWith('/category')
                  ? 'bg-primary-100 text-primary-700'
                  : 'text-foreground-700 hover:bg-background-100 hover:text-foreground-950'
              }`}
            >
              分类
              <i className="ri-arrow-down-s-line text-base" />
            </button>
            {catOpen && (
              <div className="absolute left-0 top-full w-44 pt-2">
                <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50 py-1 shadow-sm">
                  {categoryItems.map((c) => (
                    <Link
                      key={c.to}
                      to={c.to}
                      className="flex cursor-pointer items-center gap-2 px-3 py-2 text-sm text-foreground-700 transition-colors hover:bg-primary-50 hover:text-primary-700"
                    >
                      <i className={`${c.icon} text-base`} />
                      {c.label}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </nav>

        {/* Search */}
        <form
          onSubmit={submitSearch}
          className="ml-auto hidden w-full max-w-xs items-center rounded-full border border-background-200 bg-background-100 px-4 py-1.5 md:flex"
        >
          <i className="ri-search-line text-base text-foreground-400" />
          <input
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="搜索视频、分类、标签"
            className="ml-2 w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
          />
          <button
            type="submit"
            className="cursor-pointer whitespace-nowrap rounded-full bg-primary-500 px-3 py-1 text-xs font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            搜索
          </button>
        </form>

        {/* Mobile actions */}
        <div className="ml-auto flex items-center gap-2 md:hidden">
          <Link
            to="/search"
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md bg-background-100 text-foreground-700"
          >
            <i className="ri-search-line text-lg" />
          </Link>
          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md bg-background-100 text-foreground-700"
          >
            <i className={mobileOpen ? 'ri-close-line text-xl' : 'ri-menu-line text-xl'} />
          </button>
        </div>
      </div>

      {/* Mobile panel */}
      {mobileOpen && (
        <div className="border-t border-background-200 bg-background-50 px-4 pb-4 pt-3 md:hidden">
          <form
            onSubmit={submitSearch}
            className="mb-3 flex items-center rounded-full border border-background-200 bg-background-100 px-4 py-2"
          >
            <i className="ri-search-line text-base text-foreground-400" />
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索视频、分类、标签"
              className="ml-2 w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
            />
          </form>
          <div className="flex flex-col gap-1">
            {navItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className="cursor-pointer rounded-md px-3 py-2 text-sm font-medium text-foreground-700 hover:bg-background-100"
              >
                {item.label}
              </Link>
            ))}
            {categoryItems.map((c) => (
              <Link
                key={c.to}
                to={c.to}
                className="flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-foreground-700 hover:bg-background-100"
              >
                <i className={`${c.icon} text-base`} />
                {c.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}